import type { ElementDefinition } from 'shared/elements-definition';

/**
 * Shape of modules returned by the Vite glob import.
 *
 * Each definition module must export `definition: ElementDefinition`.
 */
interface DefinitionModule {
  readonly definition: ElementDefinition;
}

/**
 * All eagerly imported component definition modules.
 *
 * The glob is kept simple so it works in both dev and library builds.
 */
const modules = import.meta.glob<DefinitionModule>('../components/**/*.definition.ts', {
  eager: true
});

/**
 * All collected {@link ElementDefinition} instances.
 */
export const elementDefinitions: ReadonlyArray<ElementDefinition> = Object.values(modules).map(
  m => m.definition
);

/**
 * Converts a component name into a URL-friendly slug.
 *
 * Examples:
 * - "Card" -> "card"
 * - "IconButton" -> "icon-button"
 */
function toSlug(name: string): string {
  return name
    .replaceAll(/([a-z0-9])([A-Z])/g, '$1-$2')
    .replaceAll(/\s+/g, '-')
    .toLowerCase();
}

/**
 * Map of component slug → {@link ElementDefinition}.
 *
 * Slugs are derived from `definition.componentName`.
 */
export const elementDefinitionsBySlug: Record<string, ElementDefinition> = Object.fromEntries(
  elementDefinitions
    .filter(def => Boolean(def.componentName))
    .map(def => [toSlug(def.componentName), def])
);
